import React from 'react';
import PropTypes from 'prop-types';
import '../../styles/UsageIndicator.css';

/**
 * UsageIndicator Component
 *
 * Displays current owner usage with a progress bar.
 * Shows warning states when near or at limit.
 */
const UsageIndicator = ({
    currentOwners,
    maxOwners,
    planName,
    showUpgradeLink,
    onUpgradeClick,
    compact
}) => {
    const isUnlimited = maxOwners === null || maxOwners === undefined;
    const usagePercentage = isUnlimited ? 0 : Math.round((currentOwners / maxOwners) * 100);
    const remainingSlots = isUnlimited ? null : Math.max(0, maxOwners - currentOwners);

    const isNearLimit = !isUnlimited && usagePercentage >= 80 && usagePercentage < 100;
    const isAtLimit = !isUnlimited && currentOwners >= maxOwners;

    const getProgressBarClass = () => {
        if (isAtLimit) return 'progress-bar at-limit';
        if (isNearLimit) return 'progress-bar near-limit';
        return 'progress-bar';
    };

    const getStatusText = () => {
        if (isUnlimited) return 'Nieograniczony';
        if (isAtLimit) return 'Osiągnięto limit';
        if (isNearLimit) return 'Zbliżasz się do limitu';
        return `${remainingSlots} wolnych miejsc`;
    };

    if (compact) {
        return (
            <div className={`usage-indicator compact ${isAtLimit ? 'at-limit' : ''} ${isNearLimit ? 'near-limit' : ''}`}>
                <span className="usage-count">
                    {currentOwners}/{isUnlimited ? '∞' : maxOwners}
                </span>
                {isAtLimit && showUpgradeLink && (
                    <button className="upgrade-link-compact" onClick={onUpgradeClick}>
                        Ulepsz
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className={`usage-indicator ${isAtLimit ? 'at-limit' : ''} ${isNearLimit ? 'near-limit' : ''}`}>
            <div className="usage-header">
                <span className="usage-label">Właściciele</span>
                <span className="usage-count">
                    {currentOwners} / {isUnlimited ? '∞' : maxOwners}
                </span>
            </div>

            {!isUnlimited && (
                <div className="progress-container">
                    <div
                        className={getProgressBarClass()}
                        style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                    />
                </div>
            )}

            <div className="usage-footer">
                <span className={`status-text ${isAtLimit ? 'warning' : ''}`}>
                    {getStatusText()}
                </span>
                {planName && (
                    <span className="plan-name">Plan: {planName}</span>
                )}
            </div>

            {(isAtLimit || isNearLimit) && showUpgradeLink && (
                <button className="upgrade-link" onClick={onUpgradeClick}>
                    Ulepsz plan, aby dodać więcej właścicieli
                </button>
            )}
        </div>
    );
};

UsageIndicator.propTypes = {
    currentOwners: PropTypes.number.isRequired,
    maxOwners: PropTypes.number,
    planName: PropTypes.string,
    showUpgradeLink: PropTypes.bool,
    onUpgradeClick: PropTypes.func,
    compact: PropTypes.bool
};

UsageIndicator.defaultProps = {
    maxOwners: null,
    planName: null,
    showUpgradeLink: true,
    onUpgradeClick: () => {},
    compact: false
};

export default UsageIndicator;
